import { ReactNode } from 'react'

import { ScoredCreditCardProduct } from '@mediaingenuity/totallymoney.types'

import Box from '@ilg-totallymoney/ui/components/Box'
import Card from '@ilg-totallymoney/ui/components/Card'
import Divider from '@ilg-totallymoney/ui/components/Divider'
import Flex from '@ilg-totallymoney/ui/components/Flex'
import Heading from '@ilg-totallymoney/ui/components/Heading'

import { GuaranteedOfferType } from '@appTypes/App.interface'
import {
    CreditCardProductCategory,
    OfferType,
} from '@appTypes/EligibilityApi.interface'

import BadgesRow from '@components/BadgesRow'
import CardImage from '@components/CardImage'
import CardPositivesInfo from '@components/CardPositivesInfo'
import CreditCardContent from '@components/CreditCardContent'
import EligibilityIndicatorWithModal from '@components/EligibilityIndicatorWithModal'
import PreApprovedBadgeWithModal from '@components/PreApprovedBadgeWithModal/PreApprovedBadgeWithModal'
import RepresentativeExample from '@components/RepresentativeExample'
import TotallySureCornerBanner from '@components/TotallySureCornerBanner'

import { getProductInfoText } from '@routes/ScoredTableRoute/components/ScoredTable/components/ScoredTableItem/utils/getProductInfoText'

import getGuaranteedOfferType from '@utils/getGuaranteedOfferType'
import { getIsProductPortraitImage } from '@utils/getIsProductPortraitImage'

import { getShowPositivesList } from '../../utils/getShowPositivesList'

interface ScoredTableItemProps {
    category: CreditCardProductCategory
    isBestMatch: boolean
    item: ScoredCreditCardProduct
    onShowGuaranteedOfferModal: () => void
    onShowRepresentativeExampleModal: () => void
    renderButtons: (item: ScoredCreditCardProduct) => ReactNode
}

const ScoredTableItem = ({
    category,
    item,
    renderButtons,
    isBestMatch,
    onShowRepresentativeExampleModal,
    onShowGuaranteedOfferModal,
}: ScoredTableItemProps) => {
    const isExclusiveOffer = item.offerType === OfferType.TMExclusive
    const isMarketWideOffer = item.offerType === OfferType.MarketWide
    const isPreApproved = item.isPreApproved

    const guaranteedOfferType = getGuaranteedOfferType({
        creditCard: item,
    })

    const isMarketingOffer =
        guaranteedOfferType === GuaranteedOfferType.MarketingOffer

    const showPositiveList = getShowPositivesList({
        category,
        positives: item.bulletsPositive,
    })

    return (
        <Card
            hasBorder
            hasShadow={false}
            px={['space20', 'space30', 'space30']}
            py="space30"
            borderRadius="borderRadius30"
            data-testid={`scored-table-item-${item.productCode}`}
            overflow="hidden"
            position="relative"
        >
            {isMarketingOffer ? (
                <Box mb={['space60', '0']}>
                    <TotallySureCornerBanner
                        onClick={onShowGuaranteedOfferModal}
                    />
                </Box>
            ) : null}
            <BadgesRow
                mb="space20"
                scoredCard={item}
                isBestMatch={isBestMatch}
            />
            <Flex gridColumnGap="space20" mb="space20" gridRowGap="space10">
                <Box width={[120, 139]}>
                    <CardImage
                        cmsImageUrl={item.imageUrlLarge}
                        name={item.name}
                        isPortraitImage={getIsProductPortraitImage(
                            item.productCode
                        )}
                    />
                </Box>
                <Box>
                    <Box mb="space10">
                        <Heading as="h2" variant="20">
                            {item.name}
                        </Heading>
                    </Box>
                    <Box display={['none', 'block']}>
                        {isPreApproved ? (
                            <Box width={200}>
                                <PreApprovedBadgeWithModal />
                            </Box>
                        ) : (
                            <EligibilityIndicatorWithModal score={item.score} />
                        )}
                    </Box>
                </Box>
            </Flex>
            <Box mb="space20" display={['block', 'none']}>
                {isPreApproved ? (
                    <PreApprovedBadgeWithModal />
                ) : (
                    <EligibilityIndicatorWithModal score={item.score} />
                )}
            </Box>
            <Flex
                gridColumnGap="space30"
                justifyContent="space-between"
                mb="space30"
                flexDirection={['column', 'column', 'row']}
            >
                <Box mb={['space10', 'space30']}>
                    <CreditCardContent category={category} creditCard={item} />
                </Box>
                <Flex flexDirection="column" gridRowGap="space15">
                    {renderButtons(item)}
                </Flex>
            </Flex>
            {showPositiveList || isExclusiveOffer || isMarketWideOffer ? (
                <Box mb="space30">
                    <Divider borderStyle="dashed" spacing="space20" />
                    <CardPositivesInfo
                        positives={[
                            ...(showPositiveList ? item.bulletsPositive : []),
                            getProductInfoText(
                                item.offerHeading,
                                item.offerCopy
                            ),
                        ]}
                    />
                </Box>
            ) : null}
            <Box
                backgroundColor="grey10"
                mx="-space30"
                mb="-space30"
                p="space30"
                pt="space15"
            >
                <RepresentativeExample
                    item={item}
                    onTooltipPress={onShowRepresentativeExampleModal}
                />
            </Box>
        </Card>
    )
}

export default ScoredTableItem
